/**
 * Per-remote federation health registry.
 *
 * Pass-2 federation can fail for several reasons (dev server down, 404
 * remoteEntry, shape mismatch, version skew). Previously each failure was only
 * `console.error`'d and the user discovered it lazily by navigating into a view
 * and hitting ModuleView's per-view "Coming soon" state — there was no
 * aggregate health signal and no telemetry.
 *
 * This module tracks the load status of every federated remote, classifies the
 * failure (so a version skew is distinguishable from an unreachable host),
 * emits a telemetry event on failure, and exposes a subscribe()-able snapshot so
 * the shell chrome can surface degraded modules (e.g. a badged/disabled nav
 * entry) rather than relying solely on lazy per-view discovery.
 */

import { recordEvent } from './telemetry.ts';

export type RemoteStatus = 'ok' | 'unreachable' | 'shape_mismatch' | 'version_skew' | 'empty';

export interface RemoteHealth {
  federatedName: string;
  moduleId?: string;
  status: RemoteStatus;
  error?: string;
  updatedAt: number;
  /** Number of automatic/manual re-attempts made since the first failure. */
  attempts: number;
  /** True while a (manual or scheduled) re-attempt is in flight. */
  retrying: boolean;
  /**
   * Wall-clock time (ms epoch) the background scheduler may next auto-retry
   * this remote. Set on each failure from the exponential-backoff schedule so
   * a fleet of 404ing remotes don't all re-attempt on the same fixed tick
   * (thundering herd). Undefined for healthy entries.
   */
  nextRetryAt?: number;
}

type Listener = () => void;

/** A re-attempt loads the remote again and re-runs the registry upgrade. */
type Reattempt = () => Promise<void>;

/**
 * Background re-attempt schedule. A SCADA/Production module whose dev server
 * was briefly down should self-heal without an operator reload, but we must
 * not hammer an endpoint that is permanently 404/CORS-broken — so we stop after
 * MAX_AUTO_ATTEMPTS and leave manual retry available.
 *
 * Backoff is exponential with full ±jitter rather than a fixed interval: with a
 * fixed 30s tick every degraded remote re-attempts on the same edge, so a
 * many-module deploy where a shared CDN path 404s sends a synchronised burst of
 * requests (thundering herd) every tick. Each remote instead waits
 * `RETRY_BASE_MS * 2^attempt` (capped at RETRY_MAX_MS) ± RETRY_JITTER, so the
 * first retry lands fast (~500ms) and subsequent ones spread out and stagger
 * across remotes.
 */
const RETRY_BASE_MS = 500;
const RETRY_MAX_MS = 30_000;
const RETRY_JITTER = 0.25; // ±25%
const MAX_AUTO_ATTEMPTS = 5;
/**
 * How often the scheduler wakes to check which degraded remotes are due. The
 * per-remote `nextRetryAt` (computed from the backoff schedule) is the real
 * gate; this just bounds how stale a "due" decision can be. Kept below
 * RETRY_BASE_MS so the first, fast retry isn't delayed by the tick.
 */
const RETRY_TICK_MS = 250;

/**
 * Exponential-backoff delay for the Nth attempt (attempt is the count already
 * made), full-jittered by ±RETRY_JITTER and clamped to [0, RETRY_MAX_MS].
 */
function backoffDelayMs(attempt: number): number {
  const exp = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.max(0, attempt));
  const jitter = exp * RETRY_JITTER * (Math.random() * 2 - 1);
  return Math.max(0, Math.min(RETRY_MAX_MS, exp + jitter));
}

/**
 * Once a remote recovers (markOk), keep the `ok` entry around briefly so any
 * recovery banner can transition out gracefully, then drop it entirely so the
 * in-memory map doesn't accumulate every remote that ever flapped.
 */
const RECOVERED_RETENTION_MS = 30_000;

/**
 * Hard cap on tracked entries. A long-running shell that survives many deploys
 * could otherwise see the health map grow without bound; we keep only the most
 * recently-updated MAX_TRACKED_ENTRIES so memory stays flat.
 *
 * It has to exceed the module catalogue, not sit under it. At 20 — with a
 * catalogue of ~60 first-party modules that all register in one fan-out — a
 * RECOVERED remote's `markOk` evicted a genuinely FAILED one, and evicting a
 * degraded entry deletes three things at once: the "Module unreachable" badge,
 * the degraded banner, and the auto-retry (whose tick scans `degraded()`). The
 * observed symptom was a module that vanished from the shell with no badge, no
 * banner and no re-attempt — indistinguishable from a module that does not
 * exist. `evictionOrder` below makes that impossible independently of the
 * number, so the cap is a memory bound again rather than a silent filter.
 */
const MAX_TRACKED_ENTRIES = 200;

class RemoteHealthRegistry {
  private health = new Map<string, RemoteHealth>();
  private listeners = new Set<Listener>();
  private reattempts = new Map<string, Reattempt>();
  private autoTimer: ReturnType<typeof setTimeout> | null = null;
  /** Pending "clear recovered entry" timers keyed by federatedName. */
  private clearTimers = new Map<string, ReturnType<typeof setTimeout>>();

  markOk(federatedName: string, moduleId?: string): void {
    const wasDegraded = (this.health.get(federatedName)?.status ?? 'ok') !== 'ok';
    this.set({
      federatedName,
      moduleId,
      status: 'ok',
      updatedAt: Date.now(),
      attempts: this.health.get(federatedName)?.attempts ?? 0,
      retrying: false,
    });
    // A remote that previously failed and has now recovered shouldn't linger in
    // the map forever. Schedule its removal so degraded-entry tracking stays
    // bounded; a fresh failure before the timer fires cancels the clear.
    if (wasDegraded) this.scheduleClear(federatedName);
  }

  /** Drop a recovered entry after a grace period, unless it failed again. */
  private scheduleClear(federatedName: string): void {
    if (typeof setTimeout !== 'function') return;
    const existing = this.clearTimers.get(federatedName);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this.clearTimers.delete(federatedName);
      if (this.health.get(federatedName)?.status === 'ok') {
        this.health.delete(federatedName);
        this.listeners.forEach((l) => l());
      }
    }, RECOVERED_RETENTION_MS);
    this.clearTimers.set(federatedName, timer);
  }

  /** Cancel a pending clear (the remote degraded again before it elapsed). */
  private cancelClear(federatedName: string): void {
    const t = this.clearTimers.get(federatedName);
    if (t) {
      clearTimeout(t);
      this.clearTimers.delete(federatedName);
    }
  }

  /**
   * Evict once the cap is exceeded — RECOVERED entries first, and only then the
   * least-recently-updated.
   *
   * A healthy remote's entry is bookkeeping: it exists so a recovery banner can
   * transition out, and it is dropped on a timer anyway. A degraded one is the
   * only record that a module is missing — it drives the badge, the banner and
   * the auto-retry scan. Sorting purely by `updatedAt` let the former evict the
   * latter, which is how a failed module could disappear from the shell
   * entirely. Ordering by status first means a degraded entry can only ever be
   * evicted by another degraded entry.
   */
  private enforceCap(): void {
    if (this.health.size <= MAX_TRACKED_ENTRIES) return;
    const evictionOrder = (h: RemoteHealth) => (h.status === 'ok' ? 0 : 1);
    const ordered = Array.from(this.health.values()).sort(
      (a, b) => evictionOrder(a) - evictionOrder(b) || a.updatedAt - b.updatedAt,
    );
    const overflow = this.health.size - MAX_TRACKED_ENTRIES;
    for (let i = 0; i < overflow; i++) {
      const victim = ordered[i];
      if (!victim) continue;
      this.health.delete(victim.federatedName);
      this.cancelClear(victim.federatedName);
    }
  }

  markEmpty(federatedName: string, moduleId?: string): void {
    const attempts = this.health.get(federatedName)?.attempts ?? 0;
    this.set({
      federatedName,
      moduleId,
      status: 'empty',
      updatedAt: Date.now(),
      attempts,
      retrying: false,
      nextRetryAt: Date.now() + backoffDelayMs(attempts),
    });
    recordEvent('federation.empty', 'warn', { federatedName, moduleId });
  }

  /** Classify and record a remote-load failure, emitting telemetry. */
  markFailed(federatedName: string, error: unknown, moduleId?: string): void {
    const message = error instanceof Error ? error.message : String(error);
    const status = classify(message);
    const attempts = this.health.get(federatedName)?.attempts ?? 0;
    this.set({
      federatedName,
      moduleId,
      status,
      error: message,
      updatedAt: Date.now(),
      attempts,
      retrying: false,
      nextRetryAt: Date.now() + backoffDelayMs(attempts),
    });
    recordEvent('federation.load_failed', 'error', {
      federatedName,
      moduleId,
      status,
      error: message,
    });
  }

  /**
   * Register the function that re-loads a remote and re-runs its registry
   * upgrade. The shell's manifest bootstrap supplies one per federated module
   * so both the operator-facing "Retry" button and the background scheduler
   * can re-attempt a failed remote without reloading the whole app.
   */
  registerReattempt(federatedName: string, fn: Reattempt): void {
    this.reattempts.set(federatedName, fn);
  }

  /**
   * Re-attempt a single remote. Marks it `retrying` (so the UI can show a
   * spinner), increments the attempt counter, and re-runs the registered
   * loader. On success the loader itself calls markOk(); on failure it calls
   * markFailed(). Returns true when a reattempt was actually dispatched.
   */
  async retry(federatedName: string): Promise<boolean> {
    const fn = this.reattempts.get(federatedName);
    const current = this.health.get(federatedName);
    if (!fn || !current || current.retrying) return false;

    this.set({ ...current, retrying: true, attempts: current.attempts + 1 });
    recordEvent('federation.retry', 'info', {
      federatedName,
      moduleId: current.moduleId,
      attempt: current.attempts + 1,
    });
    try {
      await fn();
    } catch {
      /* fn() already routed the outcome through markFailed/markOk. */
    } finally {
      const after = this.health.get(federatedName);
      // Clear the in-flight flag if the loader left it set (e.g. it threw
      // before re-marking). markOk/markFailed reset `retrying` to false.
      if (after?.retrying) this.set({ ...after, retrying: false });
    }
    return true;
  }

  /**
   * Start the background re-attempt loop. Idempotent — repeated calls are a
   * no-op. The loop wakes every RETRY_TICK_MS and re-tries each degraded remote
   * whose own exponential-backoff window (`nextRetryAt`) has elapsed, that still
   * has a reattempt registered, and is under the attempt cap. Per-remote
   * jittered backoff (vs. a shared fixed interval) staggers the attempts so a
   * fleet of simultaneously-404ing remotes never re-attempt in a synchronised
   * burst.
   */
  startAutoRetry(): void {
    if (this.autoTimer || typeof setTimeout !== 'function') return;
    const tick = () => {
      const now = Date.now();
      for (const h of this.degraded()) {
        if (h.retrying) continue;
        if (h.attempts >= MAX_AUTO_ATTEMPTS) continue;
        if (!this.reattempts.has(h.federatedName)) continue;
        // Honour the per-remote backoff window. A missing nextRetryAt (older
        // entry / direct set) is treated as due immediately.
        if (h.nextRetryAt != null && h.nextRetryAt > now) continue;
        void this.retry(h.federatedName);
      }
      // Self-reschedule rather than setInterval so a long-running retry can't
      // overlap the next tick and the loop stays cancellable in one place.
      if (this.autoTimer) this.autoTimer = setTimeout(tick, RETRY_TICK_MS);
    };
    // Prime the handle before the first tick so re-entry is a no-op and the
    // first scheduled wake fires after one tick.
    this.autoTimer = setTimeout(tick, RETRY_TICK_MS);
  }

  stopAutoRetry(): void {
    if (this.autoTimer) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
  }

  /** Whether a remote can still be re-attempted manually. */
  canRetry(federatedName: string): boolean {
    return this.reattempts.has(federatedName);
  }

  private set(h: RemoteHealth): void {
    // A re-degrade (or any non-ok update) cancels a pending recovered-clear so
    // the entry survives to be surfaced again.
    if (h.status !== 'ok') this.cancelClear(h.federatedName);
    this.health.set(h.federatedName, h);
    this.enforceCap();
    this.listeners.forEach((l) => l());
  }

  get(federatedName: string): RemoteHealth | undefined {
    return this.health.get(federatedName);
  }

  /** Health keyed by moduleId (for nav-item badge lookups). */
  getByModuleId(moduleId: string): RemoteHealth | undefined {
    for (const h of this.health.values()) {
      if (h.moduleId === moduleId) return h;
    }
    return undefined;
  }

  /** All remotes that are not healthy — for surfacing degraded nav entries. */
  degraded(): RemoteHealth[] {
    return Array.from(this.health.values()).filter((h) => h.status !== 'ok');
  }

  snapshot(): RemoteHealth[] {
    return Array.from(this.health.values());
  }

  /** Drop all tracked health + reattempts (e.g. on a tenant switch). */
  reset(): void {
    this.clearTimers.forEach((t) => clearTimeout(t));
    this.clearTimers.clear();
    this.health.clear();
    this.reattempts.clear();
    this.listeners.forEach((l) => l());
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

function classify(message: string): RemoteStatus {
  const m = message.toLowerCase();
  if (m.includes('version') && (m.includes('skew') || m.includes('mismatch') || m.includes('required'))) {
    return 'version_skew';
  }
  if (
    m.includes('does not export') ||
    m.includes('no view') ||
    m.includes('shape') ||
    m.includes('manifest')
  ) {
    return 'shape_mismatch';
  }
  // Network-class failures: 404 remoteEntry, dev server down, chunk load error.
  return 'unreachable';
}

export const remoteHealth = new RemoteHealthRegistry();
